
import * as pdfMakeModule from 'pdfmake/build/pdfmake'
import * as pdfFontsModule from 'pdfmake/build/vfs_fonts'
import { formatMarkdownLite } from './textFormatting'

// --- PDF Configuration ---
const pdf = pdfMakeModule?.default ?? pdfMakeModule
const vfs =
  pdfFontsModule?.pdfMake?.vfs ||
  pdfFontsModule?.default?.pdfMake?.vfs ||
  pdfFontsModule?.default?.vfs ||
  pdfFontsModule?.vfs

if (vfs) {
  pdf.vfs = vfs
}

pdf.fonts = {
  Roboto: {
    normal: 'Roboto-Regular.ttf',
    bold: 'Roboto-Medium.ttf',
    italics: 'Roboto-Italic.ttf',
    bolditalics: 'Roboto-MediumItalic.ttf',
  },
}

// --- Helper Functions ---

async function imageUrlToDataUrl(url) {
  const response = await fetch(url)
  if (!response.ok) {
    throw new Error(`Could not load floor plan image (${response.status})`)
  }
  const blob = await response.blob()
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onloadend = () => resolve(reader.result)
    reader.onerror = reject
    reader.readAsDataURL(blob)
  })
}

// Numeric rating icon and color utilities (score: 1-5)
function getRatingIcon(score) {
  if (score >= 4.5) return '⭐⭐⭐⭐⭐';
  if (score >= 4) return '⭐⭐⭐⭐';
  if (score >= 3) return '⭐⭐⭐';
  if (score >= 2) return '⭐⭐';
  if (score >= 1) return '⭐';
  return '–';
}

function getRatingColor(score) {
  if (score >= 4.5) return '#2E7D32'; // dark green
  if (score >= 4) return '#388E3C'; // green
  if (score >= 3) return '#FF8F00'; // amber
  if (score >= 2) return '#F4511E'; // orange
  if (score >= 1) return '#C62828'; // red
  return '#757575'; // neutral grey
}

function createRatingCell(rating, t) {
  const ratingColors = {
    positive: '#2E7D32', // green
    neutral: '#FF8F00',  // amber
    negative: '#C62828', // red
  };
  const color = ratingColors[rating] || '#757575';

  return {
    stack: [
      {
        canvas: [{ type: 'ellipse', x: 25, y: 6, r1: 6, r2: 6, color }],
        alignment: 'center',
      },
      { text: t(`analysis.results.ratings.${rating}`), alignment: 'center', fontSize: 8, color: '#555', margin: [0, 2, 0, 0] },
    ],
    style: 'tableCell',
  };
}


function getPriorityContent(priority, t) {
  const priorityText = (priority || 'medium').toLowerCase();
  const priorityStyles = {
    high: { color: '#C62828' }, // red
    medium: { color: '#FF8F00' }, // amber
    low: { color: '#2E7D32' },   // green
  };
  const style = priorityStyles[priorityText] || priorityStyles.medium;

  return {
    stack: [
      {
        canvas: [{ type: 'ellipse', x: 25, y: 6, r1: 6, r2: 6, color: style.color }],
        alignment: 'center',
      },
      {
        text: t(`analysis.improvements.priority.${priorityText}`),
        alignment: 'center',
        fontSize: 8,
        color: '#555',
        margin: [0, 2, 0, 0],
      },
    ],
    style: 'tableCell',
  };
}

/**
 * A very basic converter from our `formatMarkdownLite` HTML output to pdfmake's format.
 * This is not a full HTML parser, but it handles the specific tags we use.
 */
function htmlToPdfmake(html) {
  if (!html) return ''

  // Basic transformations
  html = html
    .replace(/<strong>(.*?)<\/strong>/g, (match, p1) => `**${p1}**`)
    .replace(/<em>(.*?)<\/em>/g, (match, p1) => `_${p1}_`)
    .replace(/<br>/g, '\n')
    .replace(/<p>(.*?)<\/p>/g, '$1\n\n')
    .replace(/<li>(.*?)<\/li>/g, (match, p1) => `• ${p1}\n`)
    .replace(/<ul>/g, '')
    .replace(/<\/ul>/g, '')
    .replace(/<[^>]*>/g, '') // Strip any remaining tags
    .trim()

  // Convert markdown-style bold/italic to pdfmake format
  const segments = html.split(/(\*\*.*?\*\*|_.*?_)/g).filter(Boolean)

  return segments.map((segment) => {
    if (segment.startsWith('**') && segment.endsWith('**')) {
      return { text: segment.slice(2, -2), bold: true }
    }
    if (segment.startsWith('_') && segment.endsWith('_')) {
      return { text: segment.slice(1, -1), italics: true }
    }
    return { text: segment }
  })
}

// --- PDF Content Generation ---

function createGeneralImpression(floorPlan, t) {
  if (!floorPlan?.genereller_eindruck) return []
  return [
    { text: t('analysis.generalImpression.title'), style: 'sectionHeader' },
    { text: htmlToPdfmake(formatMarkdownLite(floorPlan.genereller_eindruck)), style: 'paragraph' },
  ]
}

function createFloorPlanAnalysis(floorPlan, t) {
  const floorPlanCategories = [
    'raumaufteilung', 'raumfluss', 'lichteinfall', 'funktionalitaet',
    'stauraum', 'privatsphaere', 'zugaenglichkeit', 'flexibilitaet', 'aussenbezug',
  ];

  const content = [];

  const categories = floorPlanCategories
    .filter((cat) => floorPlan && floorPlan[cat])
    .map((cat) => {
      const categoryData = floorPlan[cat];
      const aspects = Object.entries(categoryData)
        .filter(([key, value]) => key !== 'description' && typeof value === 'object' && value !== null)
        .map(([key, value]) => ({
          name: t(`analysis.results.aspects.${key}`),
          rating: value.rating,
          description: value.description,
        }));

      return {
        name: t(`analysis.results.categories.${cat}`),
        description: categoryData.description,
        aspects,
      };
    });

  if (categories.length === 0) return [];

  content.push({ text: t('analysis.results.title'), style: 'sectionHeader' });

  categories.forEach(category => {
    content.push({ text: category.name, style: 'categoryHeader' });
    content.push({ text: htmlToPdfmake(formatMarkdownLite(category.description)), style: 'paragraph' });

    const tableBody = [
      [
        { text: t('analysis.results.table.aspect'), style: 'tableHeader' },
        { text: t('analysis.results.table.evaluation'), style: 'tableHeader' },
        { text: t('analysis.roomEval.table.rating'), style: 'tableHeader', alignment: 'center' },
      ],
      ...category.aspects.map(aspect => [
        { text: aspect.name, style: 'tableCell', bold: true },
        { text: htmlToPdfmake(formatMarkdownLite(aspect.description)), style: 'tableCell' },
        createRatingCell(aspect.rating, t),
      ]),
    ];

    content.push({
      style: 'table',
      table: {
        widths: ['auto', '*', 'auto'],
        body: tableBody,
      },
      layout: 'lightHorizontalLines',
      margin: [0, 5, 0, 15],
    });
  });

  return content;
}

function createCostAnalysis(costAnalysis, t) {
  if (!costAnalysis) return []

  const efficiencyDetails = [
    { key: 'climateResilience', ...costAnalysis.climateResilience },
    { key: 'constructionCost', ...costAnalysis.constructionCost },
    { key: 'energyEfficiency', ...costAnalysis.energyEfficiency },
  ].filter(item => item.score && item.description)

  const body = efficiencyDetails.map(item => [
    { text: t(`analysis.costEfficiency.sections.${item.key}.title`), style: 'tableCell' },
    { text: `${item.score ?? '–'}/5`, style: 'tableCell', alignment: 'center' },
    { text: htmlToPdfmake(formatMarkdownLite(item.description)), style: 'tableCell' },
  ])

  const sections = [
    { text: t('analysis.costEfficiency.title'), style: 'sectionHeader' },
  ]

  if (Number.isFinite(costAnalysis.overallScore) && costAnalysis.overallScore > 0) {
    sections.push({
      text: `${t('analysis.costEfficiency.sections.overallScore.title')}: ${costAnalysis.overallScore}/5`,
      style: 'paragraph',
      bold: true,
    })
  }

  if (costAnalysis.costRangeQualitative?.level) {
    sections.push({
      text: `${t(`analysis.costEfficiency.costLevel.${costAnalysis.costRangeQualitative.level}`)} — ${costAnalysis.costRangeQualitative.rationale || ''}`,
      style: 'paragraph',
    })
  }

  if (costAnalysis.energyEfficiencyClass?.estimate) {
    const classLabel = costAnalysis.energyEfficiencyClass.estimate === 'unbestimmt'
      ? t('analysis.costEfficiency.energyClass.unbestimmt')
      : costAnalysis.energyEfficiencyClass.estimate
    sections.push({
      text: `${t('analysis.costEfficiency.sections.energyClass.title')}: ${classLabel} — ${costAnalysis.energyEfficiencyClass.rationale || ''}`,
      style: 'paragraph',
    })
  }

  // Cost Drivers + Quick Wins are pinned to the top of the section,
  // matching the on-screen card so the high-signal, actionable items
  // come before the long aspect descriptions.
  if (Array.isArray(costAnalysis.costDrivers) && costAnalysis.costDrivers.length > 0) {
    sections.push({ text: t('analysis.costEfficiency.sections.costDrivers.title'), style: 'sectionHeader' })
    sections.push({
      ul: costAnalysis.costDrivers.map(d => ({
        text: `[${t(`analysis.costEfficiency.severity.${d.severity}`)}] ${d.title} — ${d.description}`,
      })),
      margin: [0, 0, 0, 12],
    })
  }

  if (Array.isArray(costAnalysis.quickWins) && costAnalysis.quickWins.length > 0) {
    sections.push({ text: t('analysis.costEfficiency.sections.quickWins.title'), style: 'sectionHeader' })
    sections.push({
      ul: costAnalysis.quickWins.map(w => ({
        text: `[${t(`analysis.costEfficiency.impact.${w.estimatedImpact}`)}] ${w.title} — ${w.rationale}`,
      })),
      margin: [0, 0, 0, 12],
    })
  }

  sections.push({
    layout: 'lightHorizontalLines',
    table: {
      headerRows: 1,
      widths: ['auto', 'auto', '*'],
      body: [
        [
          { text: t('analysis.costEfficiency.table.category'), style: 'tableHeader' },
          { text: t('analysis.costEfficiency.table.score'), style: 'tableHeader', alignment: 'center' },
          { text: t('analysis.costEfficiency.table.description'), style: 'tableHeader' },
        ],
        ...body,
      ],
    },
    margin: [0, 5, 0, 15],
  })

  if (costAnalysis.summary) {
    sections.push({ text: htmlToPdfmake(formatMarkdownLite(costAnalysis.summary)), style: 'paragraph' })
  }

  return sections
}

function createRoomEvaluation(roomAnalysis, t) {
  const rooms = roomAnalysis?.results || []
  if (!rooms.length) return []

  const content = [
    { text: t('analysis.roomEval.title'), style: 'sectionHeader' },
  ]

  const aspectLocaleMap = {
    moebel: 'furniture',
    fenster: 'windows',
    energieeffizienz: 'energy',
    barrierefrei: 'barrierFree',
    stauraum: 'storage',
    flexibilitaet: 'flexibility',
  }

  rooms.forEach(room => {
    content.push({ text: room.raumName, style: 'categoryHeader' })

    if (typeof room.gesamtbewertung === 'number') {
      content.push({
        text: [
          { text: `${t('analysis.roomEval.table.rating')}: `, bold: true },
          { text: getRatingIcon(room.gesamtbewertung), color: getRatingColor(room.gesamtbewertung) },
        ],
        style: 'paragraph',
        margin: [0, 2, 0, 8],
      })
    }

    const aspects = Object.keys(room)
      .filter(key => key.endsWith('_bewertung'))
      .map(key => {
        const baseKey = key.replace('_bewertung', '')
        const bewertung = room[key]
        const beschreibung = room[`${baseKey}_beschreibung`]
        const localeKey = aspectLocaleMap[baseKey] || baseKey

        if (bewertung && typeof bewertung.score === 'number' && beschreibung) {
          return {
            name: t(`analysis.roomEval.${localeKey}`),
            score: bewertung.score,
            description: beschreibung,
          }
        }
        return null
      })
      .filter(Boolean)

    if (room.groesse) {
      const sizeText = room.groesse.tatsaechlicheGroesse !== -1
        ? `${room.groesse.tatsaechlicheGroesse}m² (${t('analysis.roomEval.recommended')}: ${room.groesse.empfohleneGroesse}m²)`
        : `${t('analysis.roomEval.sizeNotDetermined')} (${t('analysis.roomEval.recommended')}: ${room.groesse.empfohleneGroesse}m²)`

      content.push({
        text: [
          { text: `${t('analysis.roomEval.size')}: `, bold: true },
          sizeText,
        ],
        style: 'paragraph',
        margin: [0, 0, 0, 10],
      })
    }

    if (aspects.length > 0) {
      const tableBody = [
        [
          { text: t('analysis.results.table.aspect'), style: 'tableHeader' },
          { text: t('analysis.roomEval.table.rating'), style: 'tableHeader', alignment: 'center' },
          { text: t('analysis.costEfficiency.table.description'), style: 'tableHeader' },
        ],
        ...aspects.map(aspect => [
          { text: aspect.name, style: 'tableCell', bold: true },
          {
            stack: [
              { text: getRatingIcon(aspect.score), color: getRatingColor(aspect.score), alignment: 'center' },
            ],
            style: 'tableCell',
          },
          { text: htmlToPdfmake(formatMarkdownLite(aspect.description)), style: 'tableCell' },
        ]),
      ]

      content.push({
        style: 'table',
        table: {
          widths: ['auto', 'auto', '*'],
          body: tableBody,
        },
        layout: 'lightHorizontalLines',
        margin: [0, 5, 0, 15],
      })
    }
  })

  return content
}

function createSuggestions(suggestions, t) {
  if (!suggestions?.length) return []

  const priorityOrder = { high: 3, medium: 2, low: 1 }

  const sortedSuggestions = [...suggestions].sort((a, b) => {
    const priorityA = priorityOrder[a.priority] || 0
    const priorityB = priorityOrder[b.priority] || 0
    return priorityB - priorityA
  })

  const body = sortedSuggestions.map(suggestion => [
    getPriorityContent(suggestion.priority, t),
    { text: htmlToPdfmake(formatMarkdownLite(`**${suggestion.title}**\n${suggestion.description}`)), style: 'tableCell' },
    { text: t(`analysis.improvements.categories.${suggestion.category}`), style: 'tableCell' },
  ])

  return [
    { text: t('analysis.improvements.title'), style: 'sectionHeader' },
    {
      layout: 'lightHorizontalLines',
      table: {
        headerRows: 1,
        widths: ['auto', '*', 'auto'],
        body: [
          [
            { text: t('analysis.improvements.table.priority'), style: 'tableHeader' },
            { text: t('analysis.improvements.table.suggestion'), style: 'tableHeader' },
            { text: t('analysis.improvements.table.category'), style: 'tableHeader' },
          ],
          ...body,
        ],
      },
      margin: [0, 5, 0, 15],
    },
  ]
}

// --- Main PDF Generation Function ---

export async function generatePdf(analysisData, t) {
  const { imageUrl, floorPlan, costAnalysis, roomAnalysis, suggestions } = analysisData

  const brandName = t('nav.brand') || 'Floorplan AI'
  const timestamp = new Date().toLocaleString()
  const filename =
    typeof imageUrl === 'string' && imageUrl.includes('/')
      ? imageUrl.split('/').pop().replace(/^\d+-\d+-/, '')
      : 'floorplan'

  let floorPlanImage = null
  if (imageUrl) {
    try {
      floorPlanImage = await imageUrlToDataUrl(imageUrl)
    } catch (error) {
      console.warn('PDF export: floor plan image unavailable, exporting without image.', error)
    }
  }

  const content = [
    { text: t('analysis.title', 'Grundriss Analyse'), style: 'titleHeader', alignment: 'center' },
  ]

  if (floorPlanImage) {
    content.push({ image: floorPlanImage, width: 515, alignment: 'center', margin: [0, 0, 0, 20] })
  }

  const docDefinition = {
    content: [
      ...content,
      ...createGeneralImpression(floorPlan, t),
      ...createFloorPlanAnalysis(floorPlan, t),
      ...createCostAnalysis(costAnalysis, t),
      ...createRoomEvaluation(roomAnalysis, t),
      ...createSuggestions(suggestions, t),
    ],
    header: {
      columns: [
        { text: `${brandName} • ${timestamp}`, alignment: 'left' },
        { text: filename, alignment: 'right' },
      ],
      style: 'header',
      margin: [40, 20, 40, 0],
    },
    footer: (currentPage, pageCount) => ({
      columns: [
        { text: new Date().toLocaleDateString(), alignment: 'left', style: 'footer' },
        { text: `${currentPage} / ${pageCount}`, alignment: 'right', style: 'footer' },
      ],
      margin: [40, 10, 40, 0],
    }),
    info: {
      title: `${t('analysis.title')} - ${brandName} - ${timestamp}`,
      producer: brandName,
      creationDate: new Date(),
    },
    styles: {
      titleHeader: { fontSize: 22, bold: true, color: '#333', margin: [0, 0, 0, 20] },
      header: { fontSize: 10, color: '#777' },
      sectionHeader: { fontSize: 16, bold: true, color: '#00529B', margin: [0, 15, 0, 8], decoration: 'underline', decorationColor: '#00529B' },
      categoryHeader: { fontSize: 14, bold: true, color: '#333', margin: [0, 10, 0, 5] },
      paragraph: { fontSize: 10, lineHeight: 1.3, margin: [0, 0, 0, 10], color: '#444' },
      tableHeader: { bold: true, fontSize: 10, color: 'white', fillColor: '#4A90E2', margin: [5, 5, 5, 5] },
      tableCell: { fontSize: 9, lineHeight: 1.2, margin: [5, 5, 5, 5] },
      footer: { fontSize: 8, color: '#777' },
    },
    defaultStyle: {
      fontSize: 10,
      lineHeight: 1.15,
      color: '#333',
    },
  }

  try {
    pdf.createPdf(docDefinition).download(`grundriss-analyse-${new Date().toISOString().split('T')[0]}.pdf`)
  } catch (error) {
    console.error('PDF export failed:', error)
    throw error
  }
}

